import {
  useMutation,
  type ApolloCache,
  type FetchResult,
} from "@apollo/client";

import { useCustomerIdGuard } from "../../../common/hooks/useCustomerIdGuard";
import { SET_EXPECTED_ONLINE } from "../../../common/operations/mutations";
import { handleApolloError } from "../../../common/utils/handleApolloError";
import { errorNotification } from "../../../common/variables/notification";

interface SetExpectedOnlineParams {
  entityId: string;
  locationId: string;
  isExpectedOnline?: boolean;
  reason?: string;
}

interface SetExpectedOnlineResultShape {
  setExpectedOnline: {
    updatedCount: number;
  };
}

interface SetExpectedOnlineVariables {
  input: {
    customerId: string;
    entityId: string;
    isExpectedOnline?: boolean;
    reason?: string | null;
    locationId: string;
  };
}

export const useSetExpectedOnline = () => {
  const [mutate, { data, loading }] = useMutation<
    SetExpectedOnlineResultShape,
    SetExpectedOnlineVariables
  >(SET_EXPECTED_ONLINE);

  const customerId = useCustomerIdGuard();

  const setExpectedOnline = async (
    params: SetExpectedOnlineParams
  ): Promise<FetchResult<SetExpectedOnlineResultShape>> => {
    const includeToggle = typeof params.isExpectedOnline === "boolean";

    return await mutate({
      variables: {
        input: {
          customerId,
          entityId: params.entityId,
          ...(includeToggle
            ? { isExpectedOnline: params.isExpectedOnline }
            : {}),
          reason: params.reason,
          locationId: params.locationId,
        },
      },
      update: (cache: ApolloCache<SetExpectedOnlineResultShape>) => {
        try {
          const { entityId, locationId, isExpectedOnline, reason } = params;

          const detectEntity = (
            id: string
          ): "location" | "node" | "device" | "service" | "unknown" => {
            if (!id) return "unknown";

            if (id.includes("#N#") || id.includes("_N_")) return "node";

            if (id.startsWith("DE#") || id.includes("#DE#")) return "device";

            if (id.startsWith("S#") || id.includes("#S#")) return "service";

            if (id.includes("#L#") || id.includes("_L_")) return "location";

            return "unknown";
          };

          const entityType = detectEntity(entityId);
          const hasIsExpected = typeof isExpectedOnline === "boolean";
          // Attempt to parse composite service id like `${serviceType}-${deviceIdSuffix}`
          let compositeServiceType: string | null = null;
          let compositeDeviceSuffix: string | null = null;

          if (entityType === "unknown" && typeof entityId === "string") {
            const dashIdx = entityId.indexOf("-");

            if (dashIdx > 0) {
              compositeServiceType = entityId.slice(0, dashIdx);

              compositeDeviceSuffix = entityId.slice(dashIdx + 1);
            }
          }

          cache.modify({
            fields: {
              listHealthLeaves(existing) {
                if (!existing) return existing;

                const items = (existing.items ?? []).map(
                  (item: Record<string, unknown>) => {
                    const itemLocationId = item?.locationId ?? null;
                    const itemNodeId = item?.nodeId ?? null;
                    const itemDeviceId = item?.deviceId ?? null;
                    const itemServiceType = item?.serviceType ?? null;
                    const itemEntity = item?.entity ?? null; // "NODE" | "DEVICE" | "SERVICE"

                    let newIsExpected = item?.isExpectedOnline;
                    let newReason = item?.reason;
                    let shouldUpdate = false;

                    if (
                      entityType === "location" &&
                      hasIsExpected &&
                      itemLocationId === locationId
                    ) {
                      newIsExpected = isExpectedOnline;

                      newReason = isExpectedOnline
                        ? null
                        : typeof reason === "string"
                        ? reason
                        : newReason;

                      shouldUpdate = true;
                    } else if (
                      entityType === "node" &&
                      itemEntity === "NODE" &&
                      itemNodeId === entityId
                    ) {
                      if (hasIsExpected) newIsExpected = isExpectedOnline;

                      if (typeof reason !== "undefined") newReason = reason;

                      shouldUpdate = true;
                    } else if (
                      entityType === "device" &&
                      itemEntity === "DEVICE" &&
                      itemDeviceId === entityId
                    ) {
                      if (hasIsExpected) newIsExpected = isExpectedOnline;

                      if (typeof reason !== "undefined") newReason = reason;

                      shouldUpdate = true;
                    } else if (itemEntity === "SERVICE") {
                      // Try match via composite service id
                      if (compositeServiceType && compositeDeviceSuffix) {
                        const suffix =
                          typeof itemDeviceId === "string" &&
                          itemDeviceId.includes("DE#")
                            ? itemDeviceId.split("DE#")[1]
                            : null;

                        if (
                          suffix === compositeDeviceSuffix &&
                          itemServiceType === compositeServiceType
                        ) {
                          if (hasIsExpected) newIsExpected = isExpectedOnline;

                          if (typeof reason !== "undefined") newReason = reason;

                          shouldUpdate = true;
                        }
                      }
                    }

                    if (!shouldUpdate) return item;

                    return {
                      ...item,
                      isExpectedOnline: newIsExpected,
                      reason: newReason,
                    };
                  }
                );

                return { ...existing, items };
              },
            },
          });
        } catch {
          // ignore cache update errors
        }
      },
      onCompleted: result => {
        const count = result?.setExpectedOnline?.updatedCount ?? 0;

        if (count === 0) {
          errorNotification("Something went wrong. Nothing was updated.");
        }
      },
      onError: handleApolloError,
    });
  };

  return { setExpectedOnline, data, loading };
};
